import {
  Account,
  Budget,
  CashFlowDataPoint,
  Goal,
  NetWorthDataPoint,
  SpendingByCategory,
  Transaction,
  TrendGranularity,
} from "@/lib/types";

const monthFormatter = new Intl.DateTimeFormat("en-US", { month: "short" });

function formatMonthLabel(date: Date) {
  return `${monthFormatter.format(date)} ${date.getFullYear()}`;
}

function isSameMonth(date: Date, targetYear: number, targetMonth: number) {
  return date.getFullYear() === targetYear && date.getMonth() === targetMonth;
}

function normalizeTransactionDate(value: string) {
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

export function buildMonthlyCashFlow(
  transactions: Transaction[],
  months: number = 6
): CashFlowDataPoint[] {
  if (months <= 0) return [];

  const now = new Date();
  const start = new Date(now.getFullYear(), now.getMonth(), 1);
  const data: CashFlowDataPoint[] = [];

  for (let offset = months - 1; offset >= 0; offset -= 1) {
    const date = new Date(start.getFullYear(), start.getMonth() - offset, 1);
    const year = date.getFullYear();
    const month = date.getMonth();

    const monthTotals = transactions.reduce(
      (acc, transaction) => {
        const parsedDate = normalizeTransactionDate(transaction.date);
        if (!parsedDate || !isSameMonth(parsedDate, year, month)) return acc;

        const amount = Math.max(0, Math.abs(transaction.amount) - (transaction.discountAmount ?? 0));
        if (transaction.type === "income") acc.income += amount;
        if (transaction.type === "expense") acc.expenses += amount;
        return acc;
      },
      { income: 0, expenses: 0 }
    );

    const netCashFlow = monthTotals.income - monthTotals.expenses;

    data.push({
      month: formatMonthLabel(date),
      income: Number(monthTotals.income.toFixed(2)),
      expenses: Number(monthTotals.expenses.toFixed(2)),
      netCashFlow: Number(netCashFlow.toFixed(2)),
    });
  }

  return data;
}

/**
 * Attribute a transaction's amount to categories, considering item-level categories.
 * [T80P1] If a transaction has items with their own categories, each item's amount goes
 * to its category. Unitemized remainder goes to the transaction-level category.
 * Transactions without items use the transaction-level category (backward compatible).
 */
function attributeToCategories(
  transaction: Transaction,
  callback: (category: string, amount: number) => void,
): void {
  const txAmount = Math.max(0, Math.abs(transaction.amount) - (transaction.discountAmount ?? 0));
  const items = transaction.items;

  if (items && items.length > 0) {
    let itemizedTotal = 0;
    for (const item of items) {
      const itemCat = item.categoryName || item.categoryId || transaction.category || transaction.categoryId || 'Uncategorized';
      const lineTotal = Math.max(0, item.amount * (item.quantity ?? 1) - (item.discountAmount ?? 0));
      callback(itemCat, lineTotal);
      itemizedTotal += lineTotal;
    }
    // Unitemized remainder goes to the transaction-level category
    const remainder = txAmount - itemizedTotal;
    if (remainder > 0.001) {
      const txCat = transaction.category || transaction.categoryId || 'Uncategorized';
      callback(txCat, remainder);
    }
  } else {
    const category = transaction.category || transaction.categoryId || 'Uncategorized';
    callback(category, txAmount);
  }
}

export function buildSpendingByCategory(
  transactions: Transaction[],
  targetDate: Date = new Date(),
  topN: number = 5
): SpendingByCategory[] {
  const targetMonth = targetDate.getMonth();
  const targetYear = targetDate.getFullYear();

  const totals = new Map<string, number>();

  transactions.forEach((transaction) => {
    if (transaction.type !== "expense") return;

    const parsedDate = normalizeTransactionDate(transaction.date);
    if (!parsedDate || !isSameMonth(parsedDate, targetYear, targetMonth)) return;

    // [T80P1] Use item-level category attribution
    attributeToCategories(transaction, (category, amount) => {
      const current = totals.get(category) || 0;
      totals.set(category, current + amount);
    });
  });

  const sorted = Array.from(totals.entries()).sort((a, b) => b[1] - a[1]);
  const top = sorted.slice(0, topN);
  const remainder = sorted.slice(topN);

  if (remainder.length) {
    const otherTotal = remainder.reduce((sum, [, amount]) => sum + amount, 0);
    top.push(["Other", otherTotal]);
  }

  const totalAmount = top.reduce((sum, [, amount]) => sum + amount, 0);
  if (totalAmount === 0) return [];

  return top.map(([category, amount], index) => ({
    category,
    amount: Number(amount.toFixed(2)),
    percentage: Number(((amount / totalAmount) * 100).toFixed(1)),
    color: `hsl(var(--chart-${(index % 5) + 1}))`,
  }));
}

export function buildAccountBalanceByType(accounts: Account[]) {
  const totals = new Map<string, number>();

  accounts.forEach((account) => {
    const current = totals.get(account.accountType) || 0;
    totals.set(account.accountType, current + account.balance);
  });

  const entries = Array.from(totals.entries()).sort((a, b) => b[1] - a[1]);
  return entries.map(([type, balance], index) => ({
    type,
    balance: Number(balance.toFixed(2)),
    color: `hsl(var(--chart-${(index % 5) + 1}))`,
  }));
}

// ---------------------------------------------------------------------------
// Net Worth Over Time  [T47P0]
// ---------------------------------------------------------------------------

/** Asset account types whose balance represents positive net worth. */
const ASSET_TYPES = new Set([
  "checking",
  "savings",
  "investment",
  "manual",
  "crypto",
  "other",
]);

/** Liability account types whose balance reduces net worth. */
const LIABILITY_TYPES = new Set(["credit_card", "loan", "mortgage"]);

/**
 * Build a monthly net-worth time-series by summing current account balances
 * and replaying income / expense transactions backwards.
 *
 * The approach: start from today's known balances, then walk backwards month
 * by month, undoing each month's income/expenses to reconstruct historical
 * balances. This is an *approximation* (transfers, balance adjustments, and
 * new accounts added mid-period are not perfectly captured) but gives a
 * useful trend line from transaction data alone.
 */
export function buildNetWorthOverTime(
  accounts: Account[],
  transactions: Transaction[],
  months: number = 6
): NetWorthDataPoint[] {
  if (months <= 0) return [];

  // Current totals
  let currentAssets = 0;
  let currentLiabilities = 0;
  accounts.forEach((a) => {
    if (ASSET_TYPES.has(a.accountType)) currentAssets += a.balance;
    if (LIABILITY_TYPES.has(a.accountType))
      currentLiabilities += Math.abs(a.balance);
  });

  const now = new Date();
  const start = new Date(now.getFullYear(), now.getMonth(), 1);

  // Build month buckets (net income per month) walking backwards
  const monthBuckets: { year: number; month: number; netIncome: number }[] = [];
  for (let offset = 0; offset < months; offset += 1) {
    const date = new Date(start.getFullYear(), start.getMonth() - offset, 1);
    monthBuckets.push({
      year: date.getFullYear(),
      month: date.getMonth(),
      netIncome: 0,
    });
  }

  transactions.forEach((tx) => {
    const parsed = normalizeTransactionDate(tx.date);
    if (!parsed) return;
    const bucket = monthBuckets.find(
      (b) => b.year === parsed.getFullYear() && b.month === parsed.getMonth()
    );
    if (!bucket) return;
    const amount = Math.max(0, Math.abs(tx.amount) - (tx.discountAmount ?? 0));
    if (tx.type === "income") bucket.netIncome += amount;
    if (tx.type === "expense") bucket.netIncome -= amount;
  });

  // Walk backwards from current balances
  const points: NetWorthDataPoint[] = [];
  let assets = currentAssets;
  const liabilities = currentLiabilities;

  for (const bucket of monthBuckets) {
    const date = new Date(bucket.year, bucket.month, 1);
    points.push({
      date: formatMonthLabel(date),
      assets: Number(assets.toFixed(2)),
      liabilities: Number(liabilities.toFixed(2)),
      netWorth: Number((assets - liabilities).toFixed(2)),
    });
    // Undo this month's net income to get the prior month's balance
    // Positive net income increased assets, so subtract to go backwards
    assets -= bucket.netIncome;
  }

  return points.reverse(); // chronological order
}

// ---------------------------------------------------------------------------
// Savings Rate Trend  [T47P0]
// ---------------------------------------------------------------------------

export interface SavingsRateDataPoint {
  month: string;
  income: number;
  savings: number;
  rate: number;
}

/**
 * Calculate monthly savings rate = (income − expenses) / income × 100.
 * A negative rate means spending exceeded income that month.
 */
export function buildSavingsRateTrend(
  transactions: Transaction[],
  months: number = 6
): SavingsRateDataPoint[] {
  if (months <= 0) return [];

  const now = new Date();
  const start = new Date(now.getFullYear(), now.getMonth(), 1);
  const data: SavingsRateDataPoint[] = [];

  for (let offset = months - 1; offset >= 0; offset -= 1) {
    const date = new Date(start.getFullYear(), start.getMonth() - offset, 1);
    const year = date.getFullYear();
    const month = date.getMonth();

    let income = 0;
    let expenses = 0;
    transactions.forEach((tx) => {
      const parsed = normalizeTransactionDate(tx.date);
      if (!parsed || !isSameMonth(parsed, year, month)) return;
      const amount = Math.max(0, Math.abs(tx.amount) - (tx.discountAmount ?? 0));
      if (tx.type === "income") income += amount;
      if (tx.type === "expense") expenses += amount;
    });

    const savings = income - expenses;
    const rate = income > 0 ? (savings / income) * 100 : 0;

    data.push({
      month: formatMonthLabel(date),
      income: Number(income.toFixed(2)),
      savings: Number(savings.toFixed(2)),
      rate: Number(rate.toFixed(1)),
    });
  }

  return data;
}

// ---------------------------------------------------------------------------
// Budget Performance  [T47P0]
// ---------------------------------------------------------------------------

export interface BudgetPerformanceItem {
  categoryName: string;
  budgeted: number;
  spent: number;
  percentage: number;
  status: "under" | "warning" | "over";
}

/**
 * Build budget vs actual breakdown for the current month's budget.
 * Returns one entry per category budget, sorted by over-budget first.
 */
export function buildBudgetPerformance(
  budget: Budget | null
): BudgetPerformanceItem[] {
  if (!budget || !budget.categories?.length) return [];

  return budget.categories
    .map((cb) => {
      const percentage = cb.budgeted > 0 ? (cb.spent / cb.budgeted) * 100 : 0;
      let status: BudgetPerformanceItem["status"] = "under";
      if (percentage >= 100) status = "over";
      else if (percentage >= 80) status = "warning";

      return {
        categoryName: cb.categoryName,
        budgeted: cb.budgeted,
        spent: cb.spent,
        percentage: Number(percentage.toFixed(1)),
        status,
      };
    })
    .sort((a, b) => b.percentage - a.percentage);
}

// ---------------------------------------------------------------------------
// Goal Progress  [T47P0]
// ---------------------------------------------------------------------------

export interface GoalProgressItem {
  id: string;
  name: string;
  type: Goal["type"];
  targetAmount: number;
  currentAmount: number;
  progress: number;
  remaining: number;
  targetDate?: string;
  daysLeft: number | null;
  status: Goal["status"];
  onTrack: boolean;
}

/**
 * Compute progress metrics for each goal.
 * `onTrack` approximates whether current progress linearly projects to
 * reaching the target by targetDate.
 */
export function buildGoalProgress(goals: Goal[]): GoalProgressItem[] {
  if (!goals.length) return [];

  const now = new Date();

  return goals.map((g) => {
    const progress =
      g.targetAmount > 0
        ? Math.min((g.currentAmount / g.targetAmount) * 100, 100)
        : 0;
    const remaining = Math.max(g.targetAmount - g.currentAmount, 0);

    let daysLeft: number | null = null;
    let onTrack = true;

    if (g.targetDate) {
      const target = new Date(g.targetDate);
      daysLeft = Math.max(
        Math.ceil((target.getTime() - now.getTime()) / (1000 * 60 * 60 * 24)),
        0
      );

      // Calculate if on track: compare expected progress vs actual
      const created = new Date(g.createdAt);
      const totalDuration = target.getTime() - created.getTime();
      const elapsed = now.getTime() - created.getTime();
      if (totalDuration > 0 && elapsed > 0) {
        const expectedProgress = Math.min((elapsed / totalDuration) * 100, 100);
        onTrack = progress >= expectedProgress * 0.85; // 15% tolerance
      }
    }

    return {
      id: g.id,
      name: g.name,
      type: g.type,
      targetAmount: g.targetAmount,
      currentAmount: g.currentAmount,
      progress: Number(progress.toFixed(1)),
      remaining: Number(remaining.toFixed(2)),
      targetDate: g.targetDate,
      daysLeft,
      status: g.status,
      onTrack,
    };
  });
}

// ---------------------------------------------------------------------------
// Spending Trend (multi-category over time)  [T47P0]
// ---------------------------------------------------------------------------

export interface SpendingTrendDataPoint {
  month: string;
  [category: string]: string | number;
}

/**
 * Build a month-by-month breakdown of spending for the top N categories.
 * Returns data suitable for a multi-series area/line chart.
 */
export function buildSpendingTrend(
  transactions: Transaction[],
  months: number = 6,
  topN: number = 5
): { data: SpendingTrendDataPoint[]; categories: string[] } {
  if (months <= 0) return { data: [], categories: [] };

  const now = new Date();
  const start = new Date(now.getFullYear(), now.getMonth(), 1);

  // First pass: identify the top N categories by total spend
  const categoryTotals = new Map<string, number>();
  transactions.forEach((tx) => {
    if (tx.type !== "expense") return;
    // [T80P1] Item-level category attribution for trend identification
    attributeToCategories(tx, (cat, amount) => {
      categoryTotals.set(cat, (categoryTotals.get(cat) || 0) + amount);
    });
  });

  const topCategories = Array.from(categoryTotals.entries())
    .sort((a, b) => b[1] - a[1])
    .slice(0, topN)
    .map(([cat]) => cat);

  // Second pass: build monthly data
  const data: SpendingTrendDataPoint[] = [];

  for (let offset = months - 1; offset >= 0; offset -= 1) {
    const date = new Date(start.getFullYear(), start.getMonth() - offset, 1);
    const year = date.getFullYear();
    const month = date.getMonth();

    const point: SpendingTrendDataPoint = { month: formatMonthLabel(date) };
    topCategories.forEach((cat) => {
      point[cat] = 0;
    });

    transactions.forEach((tx) => {
      if (tx.type !== "expense") return;
      const parsed = normalizeTransactionDate(tx.date);
      if (!parsed || !isSameMonth(parsed, year, month)) return;

      // [T80P1] Item-level category attribution
      attributeToCategories(tx, (cat, amount) => {
        if (topCategories.includes(cat)) {
          (point[cat] as number) += amount;
        }
      });
    });

    // Round the values
    topCategories.forEach((cat) => {
      point[cat] = Number((point[cat] as number).toFixed(2));
    });

    data.push(point);
  }

  return { data, categories: topCategories };
}

// ---------------------------------------------------------------------------
// Recurring Expenses Summary  [T47P0]
// ---------------------------------------------------------------------------

export interface RecurringExpenseItem {
  description: string;
  amount: number;
  frequency: string;
  monthlyEstimate: number;
  yearlyEstimate: number;
  category: string;
  lastDate: string;
}

const FREQUENCY_MULTIPLIER: Record<string, { monthly: number; yearly: number }> =
  {
    daily: { monthly: 30, yearly: 365 },
    weekly: { monthly: 4.33, yearly: 52 },
    biweekly: { monthly: 2.17, yearly: 26 },
    monthly: { monthly: 1, yearly: 12 },
    quarterly: { monthly: 0.33, yearly: 4 },
    yearly: { monthly: 0.083, yearly: 1 },
  };

/**
 * Identify recurring expenses and estimate their monthly/yearly cost.
 * Groups by description + frequency pattern and takes the latest amount.
 */
export function buildRecurringExpensesSummary(
  transactions: Transaction[]
): RecurringExpenseItem[] {
  const recurring = transactions.filter(
    (tx) => tx.type === "expense" && tx.isRecurring && tx.recurringPattern
  );

  // Group by description (normalize casing)
  const groups = new Map<
    string,
    { tx: Transaction; dates: Date[] }
  >();

  recurring.forEach((tx) => {
    const key = tx.description.toLowerCase().trim();
    const existing = groups.get(key);
    const parsed = normalizeTransactionDate(tx.date);
    if (!parsed) return;

    if (existing) {
      existing.dates.push(parsed);
      // Keep the latest transaction data
      const existingDate = normalizeTransactionDate(existing.tx.date);
      if (existingDate && parsed > existingDate) {
        existing.tx = tx;
      }
    } else {
      groups.set(key, { tx, dates: [parsed] });
    }
  });

  return Array.from(groups.values())
    .map(({ tx }) => {
      const freq = (typeof tx.recurringPattern === 'string' ? tx.recurringPattern : tx.recurringPattern?.frequency) || 'monthly';
      const mult = FREQUENCY_MULTIPLIER[freq as string] || FREQUENCY_MULTIPLIER.monthly;
      const amount = Math.abs(tx.amount);

      return {
        description: tx.description,
        amount,
        frequency: freq,
        monthlyEstimate: Number((amount * mult.monthly).toFixed(2)),
        yearlyEstimate: Number((amount * mult.yearly).toFixed(2)),
        category: tx.category || tx.categoryId || "Uncategorized",
        lastDate: tx.date,
      };
    })
    .sort((a, b) => b.yearlyEstimate - a.yearlyEstimate);
}

// ---------------------------------------------------------------------------
// Financial Health Score  [T47P0]
// ---------------------------------------------------------------------------

export interface FinancialHealthFactor {
  name: string;
  score: number; // 0-100
  weight: number;
  status: "excellent" | "good" | "fair" | "poor";
  description: string;
}

export interface FinancialHealthResult {
  overallScore: number;
  grade: "A" | "B" | "C" | "D" | "F";
  factors: FinancialHealthFactor[];
}

function scoreFactor(
  value: number,
  thresholds: [number, number, number, number]
): { score: number; status: FinancialHealthFactor["status"] } {
  // thresholds: [excellent, good, fair, poor cutoffs]
  if (value >= thresholds[0]) return { score: 95, status: "excellent" };
  if (value >= thresholds[1]) return { score: 75, status: "good" };
  if (value >= thresholds[2]) return { score: 50, status: "fair" };
  return { score: 25, status: "poor" };
}

/**
 * Calculate a composite financial health score (0-100) based on:
 * 1. Savings rate — % of income saved
 * 2. Budget adherence — how close spending stays to budgets
 * 3. Debt-to-asset ratio — liabilities / assets
 * 4. Goal progress — average progress across active goals
 * 5. Emergency fund — months of expenses covered by liquid assets
 *
 * Each factor is scored 0–100 and weighted to produce an overall grade.
 */
export function buildFinancialHealthScore(
  accounts: Account[],
  transactions: Transaction[],
  budget: Budget | null,
  goals: Goal[]
): FinancialHealthResult {
  const factors: FinancialHealthFactor[] = [];

  // --- 1. Savings Rate (weight 25%) ---
  const now = new Date();
  const thisMonth = now.getMonth();
  const thisYear = now.getFullYear();
  let monthlyIncome = 0;
  let monthlyExpenses = 0;
  transactions.forEach((tx) => {
    const d = normalizeTransactionDate(tx.date);
    if (!d || !isSameMonth(d, thisYear, thisMonth)) return;
    if (tx.type === "income") monthlyIncome += Math.max(0, Math.abs(tx.amount) - (tx.discountAmount ?? 0));
    if (tx.type === "expense") monthlyExpenses += Math.max(0, Math.abs(tx.amount) - (tx.discountAmount ?? 0));
  });
  const savingsRate =
    monthlyIncome > 0
      ? ((monthlyIncome - monthlyExpenses) / monthlyIncome) * 100
      : 0;
  const sr = scoreFactor(savingsRate, [20, 10, 0, -100]);
  factors.push({
    name: "Savings Rate",
    score: sr.score,
    weight: 25,
    status: sr.status,
    description:
      savingsRate >= 0
        ? `Saving ${savingsRate.toFixed(1)}% of income this month`
        : `Spending ${Math.abs(savingsRate).toFixed(1)}% more than income`,
  });

  // --- 2. Budget Adherence (weight 20%) ---
  let budgetScore = 75; // Default if no budget
  let budgetStatus: FinancialHealthFactor["status"] = "good";
  let budgetDesc = "No budget set for this month";
  if (budget && budget.categories.length > 0) {
    const totalBudgeted = budget.categories.reduce(
      (s, c) => s + c.budgeted,
      0
    );
    const totalSpent = budget.categories.reduce((s, c) => s + c.spent, 0);
    const adherence =
      totalBudgeted > 0
        ? Math.max(0, 100 - ((totalSpent - totalBudgeted) / totalBudgeted) * 100)
        : 100;
    const bs = scoreFactor(adherence, [90, 70, 50, 0]);
    budgetScore = bs.score;
    budgetStatus = bs.status;
    const pct = totalBudgeted > 0 ? (totalSpent / totalBudgeted) * 100 : 0;
    budgetDesc = `Using ${pct.toFixed(0)}% of total budget`;
  }
  factors.push({
    name: "Budget Adherence",
    score: budgetScore,
    weight: 20,
    status: budgetStatus,
    description: budgetDesc,
  });

  // --- 3. Debt-to-Asset Ratio (weight 25%) ---
  let totalAssets = 0;
  let totalLiabilities = 0;
  accounts.forEach((a) => {
    if (ASSET_TYPES.has(a.accountType)) totalAssets += a.balance;
    if (LIABILITY_TYPES.has(a.accountType))
      totalLiabilities += Math.abs(a.balance);
  });
  // Lower ratio is better: 0% = excellent, <36% = good (per DTI guidelines)
  const debtRatio =
    totalAssets > 0 ? (totalLiabilities / totalAssets) * 100 : 0;
  const inverted = Math.max(0, 100 - debtRatio); // Invert so higher is better
  const dr = scoreFactor(inverted, [80, 64, 50, 0]);
  factors.push({
    name: "Debt-to-Asset Ratio",
    score: dr.score,
    weight: 25,
    status: dr.status,
    description:
      totalAssets > 0
        ? `${debtRatio.toFixed(1)}% debt relative to assets`
        : "No account data available",
  });

  // --- 4. Goal Progress (weight 15%) ---
  const activeGoals = goals.filter((g) => g.status === "active");
  let goalScore = 75;
  let goalStatus: FinancialHealthFactor["status"] = "good";
  let goalDesc = "No active goals";
  if (activeGoals.length > 0) {
    const avgProgress =
      activeGoals.reduce((sum, g) => {
        const pct =
          g.targetAmount > 0 ? (g.currentAmount / g.targetAmount) * 100 : 0;
        return sum + Math.min(pct, 100);
      }, 0) / activeGoals.length;
    const gs = scoreFactor(avgProgress, [75, 50, 25, 0]);
    goalScore = gs.score;
    goalStatus = gs.status;
    goalDesc = `${activeGoals.length} active goal${activeGoals.length > 1 ? "s" : ""}, ${avgProgress.toFixed(0)}% avg progress`;
  }
  factors.push({
    name: "Goal Progress",
    score: goalScore,
    weight: 15,
    status: goalStatus,
    description: goalDesc,
  });

  // --- 5. Emergency Fund (weight 15%) ---
  const liquidAssets = accounts
    .filter((a) => ["checking", "savings"].includes(a.accountType))
    .reduce((sum, a) => sum + a.balance, 0);
  // Average monthly expenses from last 3 months
  let totalExpenses3m = 0;
  let monthsWithData = 0;
  for (let i = 0; i < 3; i += 1) {
    const date = new Date(now.getFullYear(), now.getMonth() - i, 1);
    let monthExp = 0;
    transactions.forEach((tx) => {
      if (tx.type !== "expense") return;
      const d = normalizeTransactionDate(tx.date);
      if (d && isSameMonth(d, date.getFullYear(), date.getMonth())) {
        monthExp += Math.max(0, Math.abs(tx.amount) - (tx.discountAmount ?? 0));
      }
    });
    if (monthExp > 0) {
      totalExpenses3m += monthExp;
      monthsWithData += 1;
    }
  }
  const avgMonthlyExpense =
    monthsWithData > 0 ? totalExpenses3m / monthsWithData : 0;
  const monthsCovered =
    avgMonthlyExpense > 0 ? liquidAssets / avgMonthlyExpense : 0;
  // Recommended: 3-6 months emergency fund
  const ef = scoreFactor(monthsCovered, [6, 3, 1, 0]);
  factors.push({
    name: "Emergency Fund",
    score: ef.score,
    weight: 15,
    status: ef.status,
    description:
      avgMonthlyExpense > 0
        ? `${monthsCovered.toFixed(1)} months of expenses covered`
        : "Not enough data to estimate",
  });

  // --- Overall Score ---
  const totalWeight = factors.reduce((s, f) => s + f.weight, 0);
  const overallScore =
    totalWeight > 0
      ? factors.reduce((s, f) => s + (f.score * f.weight) / totalWeight, 0)
      : 0;

  let grade: FinancialHealthResult["grade"] = "F";
  if (overallScore >= 90) grade = "A";
  else if (overallScore >= 75) grade = "B";
  else if (overallScore >= 60) grade = "C";
  else if (overallScore >= 40) grade = "D";

  return {
    overallScore: Number(overallScore.toFixed(0)),
    grade,
    factors,
  };
}

// ---------------------------------------------------------------------------
// Transaction Insights  [T61P0]
// ---------------------------------------------------------------------------

export type TransactionType = "income" | "expense" | "transfer";

export interface InsightByType {
  type: TransactionType;
  total: number;
  count: number;
  percentage: number; // % of total transaction count
  color: string;
}

const TYPE_COLORS: Record<TransactionType, string> = {
  income: "hsl(var(--chart-2))",
  expense: "hsl(var(--chart-1))",
  transfer: "hsl(var(--chart-3))",
};

/**
 * Summarise transactions by type — total amount spent/received and count per type.
 * Percentages represent share of the total transaction count.
 */
export function buildInsightsByType(
  transactions: Transaction[]
): InsightByType[] {
  if (!transactions.length) return [];

  const totals: Record<TransactionType, { total: number; count: number }> = {
    income: { total: 0, count: 0 },
    expense: { total: 0, count: 0 },
    transfer: { total: 0, count: 0 },
  };

  transactions.forEach((tx) => {
    const t = tx.type as TransactionType;
    if (!totals[t]) return;
    totals[t].total += Math.max(0, Math.abs(tx.amount) - (tx.discountAmount ?? 0));
    totals[t].count += 1;
  });

  const totalCount = transactions.length;

  return (Object.keys(totals) as TransactionType[])
    .filter((t) => totals[t].count > 0)
    .map((t) => ({
      type: t,
      total: Number(totals[t].total.toFixed(2)),
      count: totals[t].count,
      percentage: Number(((totals[t].count / totalCount) * 100).toFixed(1)),
      color: TYPE_COLORS[t],
    }));
}

export interface InsightByAccount {
  accountId: string;
  accountName: string;
  total: number; // absolute sum of amounts
  count: number;
  color: string;
}

/**
 * Summarise transactions by source account — total volume and count per account.
 * Sorted descending by total volume.
 */
export function buildInsightsByAccount(
  transactions: Transaction[],
  accounts: Account[]
): InsightByAccount[] {
  if (!transactions.length) return [];

  const totals = new Map<string, { total: number; count: number }>();

  transactions.forEach((tx) => {
    const id = tx.accountId || "unknown";
    const existing = totals.get(id) || { total: 0, count: 0 };
    existing.total += Math.max(0, Math.abs(tx.amount) - (tx.discountAmount ?? 0));
    existing.count += 1;
    totals.set(id, existing);
  });

  const accountMap = new Map(accounts.map((a) => [a.id, a]));

  return Array.from(totals.entries())
    .sort((a, b) => b[1].total - a[1].total)
    .map(([id, { total, count }], index) => {
      const acc = accountMap.get(id);
      return {
        accountId: id,
        accountName: acc?.name ?? "Unknown Account",
        total: Number(total.toFixed(2)),
        count,
        color: `hsl(var(--chart-${(index % 5) + 1}))`,
      };
    });
}

export interface RecurringBreakdown {
  recurring: { count: number; total: number; percentage: number };
  oneTime: { count: number; total: number; percentage: number };
}

/**
 * Split transactions into recurring vs one-time breakdowns.
 */
export function buildRecurringBreakdown(
  transactions: Transaction[]
): RecurringBreakdown {
  const empty = { count: 0, total: 0, percentage: 0 };
  if (!transactions.length)
    return { recurring: empty, oneTime: empty };

  let rCount = 0;
  let rTotal = 0;
  let oCount = 0;
  let oTotal = 0;

  transactions.forEach((tx) => {
    const amt = Math.max(0, Math.abs(tx.amount) - (tx.discountAmount ?? 0));
    if (tx.isRecurring) {
      rCount += 1;
      rTotal += amt;
    } else {
      oCount += 1;
      oTotal += amt;
    }
  });

  const total = rCount + oCount;
  return {
    recurring: {
      count: rCount,
      total: Number(rTotal.toFixed(2)),
      percentage: Number(((rCount / total) * 100).toFixed(1)),
    },
    oneTime: {
      count: oCount,
      total: Number(oTotal.toFixed(2)),
      percentage: Number(((oCount / total) * 100).toFixed(1)),
    },
  };
}

export interface CategoryTotalItem {
  category: string;
  total: number;
  count: number;
  color: string;
}

/**
 * Sum expense transactions by category across ALL provided transactions (no date
 * filter — the caller controls the range by passing pre-filtered transactions or
 * API-aggregated data). Useful for the Transaction Insights "By Category" tab.
 */
export function buildCategoryTotals(
  transactions: Transaction[],
  topN: number = 7
): CategoryTotalItem[] {
  const totals = new Map<string, { total: number; count: number }>();

  transactions.forEach((tx) => {
    if (tx.type !== "expense") return;
    // [T80P1] Item-level category attribution
    attributeToCategories(tx, (cat, amount) => {
      const curr = totals.get(cat) || { total: 0, count: 0 };
      curr.total += amount;
      curr.count += 1;
      totals.set(cat, curr);
    });
  });

  return Array.from(totals.entries())
    .sort((a, b) => b[1].total - a[1].total)
    .slice(0, topN)
    .map(([category, { total, count }], index) => ({
      category,
      total: Number(total.toFixed(2)),
      count,
      color: `hsl(var(--chart-${(index % 5) + 1}))`,
    }));
}

// --- [T70P2-c] Spending Over Time (Trends) ---

export interface SpendingOverTimePoint {
  date: string;
  income: number;
  expense: number;
  net: number;
}

const weekFormatter = new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric' });

// Epoch reference for 3d/2w bucketing (2020-01-06 = a Monday)
const EPOCH_MS = new Date('2020-01-06T00:00:00Z').getTime();
const DAY_MS = 86_400_000;

/** Resolve 'auto' to the actual granularity given periodDays. */
export function resolveGranularity(
  granularity: TrendGranularity | undefined,
  periodDays: number | null,
): Exclude<TrendGranularity, 'auto'> {
  if (granularity && granularity !== 'auto') return granularity;
  if (periodDays !== null && periodDays <= 45) return '1d';
  if (periodDays !== null && periodDays <= 180) return '1w';
  return '1m';
}

/** Compute a sortable bucket key for a given date and granularity. */
function bucketKey(d: Date, gran: Exclude<TrendGranularity, 'auto'>): string {
  switch (gran) {
    case '1d':
      return d.toISOString().slice(0, 10);
    case '3d': {
      const daysSinceEpoch = Math.floor((d.getTime() - EPOCH_MS) / DAY_MS);
      const windowStart = new Date(EPOCH_MS + Math.floor(daysSinceEpoch / 3) * 3 * DAY_MS);
      return windowStart.toISOString().slice(0, 10);
    }
    case '1w': {
      const day = d.getDay();
      const diff = d.getDate() - day + (day === 0 ? -6 : 1);
      const weekStart = new Date(d);
      weekStart.setDate(diff);
      weekStart.setHours(0, 0, 0, 0);
      return weekStart.toISOString().slice(0, 10);
    }
    case '2w': {
      const daysSinceEpoch = Math.floor((d.getTime() - EPOCH_MS) / DAY_MS);
      const windowStart = new Date(EPOCH_MS + Math.floor(daysSinceEpoch / 14) * 14 * DAY_MS);
      return windowStart.toISOString().slice(0, 10);
    }
    case '1m':
      return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
    case '3m': {
      const q = Math.floor(d.getMonth() / 3) + 1;
      return `${d.getFullYear()}-Q${q}`;
    }
    // [T270-PY] 6M and 1Y bucketing — half-year and full-year, respectively
    case '6m': {
      const half = d.getMonth() < 6 ? 'H1' : 'H2';
      return `${d.getFullYear()}-${half}`;
    }
    case '1y':
      return `${d.getFullYear()}`;
  }
}

/** Format a bucket key into a human-readable label based on granularity. */
function bucketLabel(key: string, gran: Exclude<TrendGranularity, 'auto'>): string {
  switch (gran) {
    case '1d': {
      const d = new Date(key);
      return weekFormatter.format(d);
    }
    case '3d': {
      const start = new Date(key);
      const end = new Date(start);
      end.setDate(start.getDate() + 2);
      return `${weekFormatter.format(start)}–${weekFormatter.format(end).split(' ')[1]}`;
    }
    case '1w': {
      const start = new Date(key);
      const end = new Date(start);
      end.setDate(start.getDate() + 6);
      return `${weekFormatter.format(start)}–${weekFormatter.format(end).split(' ')[1]}`;
    }
    case '2w': {
      const start = new Date(key);
      const end = new Date(start);
      end.setDate(start.getDate() + 13);
      return `${weekFormatter.format(start)}–${weekFormatter.format(end).split(' ')[1]}`;
    }
    case '1m': {
      const [y, m] = key.split('-');
      const date = new Date(Number(y), Number(m) - 1, 1);
      return formatMonthLabel(date);
    }
    case '3m': {
      const [y, q] = key.split('-Q');
      return `Q${q} ${y}`;
    }
    // [T270-PY] 6M = "H1 2026" / "H2 2026"; 1Y = "2026"
    case '6m': {
      const [y, h] = key.split('-');
      return `${h} ${y}`;
    }
    case '1y':
      return key;
}
}

/** Advance a date key to the next bucket boundary. */
function nextBucketKey(key: string, gran: Exclude<TrendGranularity, 'auto'>): string {
  switch (gran) {
    case '1d': {
      const d = new Date(key);
      d.setDate(d.getDate() + 1);
      return d.toISOString().slice(0, 10);
    }
    case '3d': {
      const d = new Date(key);
      d.setDate(d.getDate() + 3);
      return d.toISOString().slice(0, 10);
    }
    case '1w': {
      const d = new Date(key);
      d.setDate(d.getDate() + 7);
      return d.toISOString().slice(0, 10);
    }
    case '2w': {
      const d = new Date(key);
      d.setDate(d.getDate() + 14);
      return d.toISOString().slice(0, 10);
    }
    case '1m': {
      const [y, m] = key.split('-').map(Number);
      const nm = m === 12 ? 1 : m + 1;
      const ny = m === 12 ? y + 1 : y;
      return `${ny}-${String(nm).padStart(2, '0')}`;
    }
    case '3m': {
      const [y, q] = key.split('-Q').map(Number);
      const nq = q === 4 ? 1 : q + 1;
      const ny = q === 4 ? y + 1 : y;
      return `${ny}-Q${nq}`;
    }
    // [T270-PY] Half-year + year advances
    case '6m': {
      const [y, h] = key.split('-');
      if (h === 'H1') return `${y}-H2`;
      return `${Number(y) + 1}-H1`;
    }
    case '1y':
      return `${Number(key) + 1}`;
  }
}

/**
 * Build a time-series of income vs expense for the given period.
 *
 * Optional 3rd parameter `granularity`:
 * - `'auto'` or omitted: ≤90d → weekly, >90d → monthly (original behavior)
 * - `'1d'` | `'3d'` | `'1w'` | `'2w'` | `'1m'` | `'3m'`: force specific bucketing
 *
 * Gap-fill: missing periods between first and last bucket get `{income:0, expense:0, net:0}`.
 */
export function buildSpendingOverTime(
  transactions: Transaction[],
  periodDays: number | null,
  granularity?: TrendGranularity,
): SpendingOverTimePoint[] {
  if (!transactions.length) return [];

  const gran = resolveGranularity(granularity, periodDays);
  const buckets = new Map<string, { income: number; expense: number }>();

  for (const tx of transactions) {
    const d = normalizeTransactionDate(tx.date);
    if (!d) continue;
    if (tx.type === 'transfer') continue;

    const key = bucketKey(d, gran);
    const bucket = buckets.get(key) ?? { income: 0, expense: 0 };
    const amount = Math.max(0, Math.abs(tx.amount) - (tx.discountAmount ?? 0));
    if (tx.type === 'income') bucket.income += amount;
    else if (tx.type === 'expense') bucket.expense += amount;
    buckets.set(key, bucket);
  }

  if (buckets.size === 0) return [];

  // Sort keys and gap-fill
  const sortedKeys = Array.from(buckets.keys()).sort((a, b) => a.localeCompare(b));
  const firstKey = sortedKeys[0];
  const lastKey = sortedKeys[sortedKeys.length - 1];
  const allKeys: string[] = [];
  let cursor = firstKey;
  while (cursor.localeCompare(lastKey) <= 0) {
    allKeys.push(cursor);
    cursor = nextBucketKey(cursor, gran);
  }

  return allKeys.map((key) => {
    const { income, expense } = buckets.get(key) ?? { income: 0, expense: 0 };
    return {
      date: bucketLabel(key, gran),
      income: Number(income.toFixed(2)),
      expense: Number(expense.toFixed(2)),
      net: Number((income - expense).toFixed(2)),
    };
  });
}
